import { createContext, useContext, useState, ReactNode, useCallback } from "react";
import { inProductionMode, getAppMode, displayName } from "@/lib/demoMode";

// ── Battle Team types ────────────────────────────────────────────────
export type SlotStrategy = "fast-cycle" | "heavy-hitter" | "bulky-wall";
export type BattleRole = "Lead" | "Sweeper" | "Wall" | "Pivot" | "Tech" | "Special Attacker";

export interface BattleSlot {
  /** National Dex number for Nexus glyph lookup. */
  dexNumber: number;
  species: string;
  cp: number;
  level: number;
  ivPercent: number; // 0-100
  isLucky?: boolean;
  isShadow?: boolean;
  isShiny?: boolean;
  isHundo?: boolean;
  fastMove: string;
  chargeMoves: string[]; // 1-2
  role: BattleRole;
  strategy: SlotStrategy;
  // Type info for sim, primary type only is enough for our deterministic sim.
  types: string[]; // e.g. ["fighting", "ghost"]
  baseAtk: number;  // 100-300 typical
  baseDef: number;
  baseHp: number;
}

export interface BattleTeam {
  name: string;
  trainer: string;
  slots: BattleSlot[]; // up to 6
  rating: number; // BP
}

// ── Pre-seeded teams ─────────────────────────────────────────────────
// Prototype demo data only. The import.meta.env.PROD ternary causes
// Vite + terser to eliminate the seed literals from the production
// bundle entirely. In production the team starts empty and the user
// builds it from their roster.
const EMPTY_BATTLE_TEAM: BattleTeam = { name: "Your Team", trainer: "You", slots: [], rating: 0 };

const INFERNAL_TEAM: BattleTeam = import.meta.env.PROD ? EMPTY_BATTLE_TEAM : {
  name: "InfernalVamp's Fire Squad",
  trainer: "InfernalVamp",
  rating: 7840,
  slots: [
    {
      dexNumber: 979, species: "Annihilape", cp: 3681, level: 50, ivPercent: 96,
      isLucky: true, fastMove: "Low Kick", chargeMoves: ["Low Sweep", "Ice Punch"],
      role: "Lead", strategy: "fast-cycle",
      types: ["fighting", "ghost"], baseAtk: 270, baseDef: 195, baseHp: 195,
    },
    {
      dexNumber: 889, species: "Zamazenta", cp: 3720, level: 50, ivPercent: 100,
      isLucky: true, isShiny: true, isHundo: true,
      fastMove: "Metal Claw", chargeMoves: ["Close Combat", "Behemoth Bash"],
      role: "Sweeper", strategy: "heavy-hitter",
      types: ["fighting", "steel"], baseAtk: 285, baseDef: 230, baseHp: 220,
    },
    {
      dexNumber: 487, species: "Giratina", cp: 3565, level: 48, ivPercent: 91,
      fastMove: "Shadow Claw", chargeMoves: ["Shadow Force", "Dragon Pulse"],
      role: "Wall", strategy: "bulky-wall",
      types: ["ghost", "dragon"], baseAtk: 225, baseDef: 220, baseHp: 280,
    },
    {
      dexNumber: 382, species: "Kyogre", cp: 2921, level: 35, ivPercent: 67,
      isShadow: true, isShiny: true,
      fastMove: "Waterfall", chargeMoves: ["Origin Pulse", "Surf"],
      role: "Special Attacker", strategy: "heavy-hitter",
      types: ["water"], baseAtk: 270, baseDef: 230, baseHp: 220,
    },
    {
      dexNumber: 1000, species: "Gholdengo", cp: 3644, level: 49, ivPercent: 93,
      isLucky: true, fastMove: "Hex", chargeMoves: ["Make It Rain", "Shadow Ball"],
      role: "Pivot", strategy: "fast-cycle",
      types: ["steel", "ghost"], baseAtk: 240, baseDef: 200, baseHp: 220,
    },
    {
      dexNumber: 462, species: "Magnezone", cp: 3605, level: 50, ivPercent: 89,
      isLucky: true, fastMove: "Spark", chargeMoves: ["Wild Charge", "Mirror Shot"],
      role: "Tech", strategy: "fast-cycle",
      types: ["electric", "steel"], baseAtk: 245, baseDef: 215, baseHp: 180,
    },
  ],
};

const EMPTY_OPP_TEAM_INIT: BattleTeam = { name: "Opponent", trainer: "", slots: [], rating: 0 };

const MIA_TEAM: BattleTeam = import.meta.env.PROD ? EMPTY_OPP_TEAM_INIT : {
  name: "Mia's Dragon Storm",
  trainer: "Mia",
  rating: 7610,
  slots: [
    {
      dexNumber: 445, species: "Garchomp", cp: 3600, level: 47, ivPercent: 94,
      fastMove: "Mud Shot", chargeMoves: ["Earthquake", "Outrage"],
      role: "Sweeper", strategy: "heavy-hitter",
      types: ["dragon", "ground"], baseAtk: 260, baseDef: 195, baseHp: 240,
    },
    {
      dexNumber: 468, species: "Togekiss", cp: 3201, level: 45, ivPercent: 88,
      fastMove: "Charm", chargeMoves: ["Dazzling Gleam", "Aerial Ace"],
      role: "Wall", strategy: "bulky-wall",
      types: ["fairy", "flying"], baseAtk: 225, baseDef: 215, baseHp: 195,
    },
    {
      dexNumber: 376, species: "Metagross", cp: 3589, level: 48, ivPercent: 91,
      fastMove: "Bullet Punch", chargeMoves: ["Meteor Mash", "Earthquake"],
      role: "Lead", strategy: "fast-cycle",
      types: ["steel", "psychic"], baseAtk: 257, baseDef: 228, baseHp: 190,
    },
    {
      dexNumber: 248, species: "Tyranitar", cp: 3463, level: 46, ivPercent: 86,
      fastMove: "Smack Down", chargeMoves: ["Stone Edge", "Crunch"],
      role: "Pivot", strategy: "heavy-hitter",
      types: ["rock", "dark"], baseAtk: 251, baseDef: 207, baseHp: 200,
    },
    {
      dexNumber: 373, species: "Salamence", cp: 3478, level: 46, ivPercent: 90,
      fastMove: "Dragon Tail", chargeMoves: ["Outrage", "Hydro Pump"],
      role: "Special Attacker", strategy: "heavy-hitter",
      types: ["dragon", "flying"], baseAtk: 277, baseDef: 168, baseHp: 220,
    },
    {
      dexNumber: 479, species: "Rotom-Wash", cp: 3210, level: 45, ivPercent: 89,
      fastMove: "Hex", chargeMoves: ["Hydro Pump", "Thunder Shock"],
      role: "Tech", strategy: "fast-cycle",
      types: ["electric", "water"], baseAtk: 218, baseDef: 200, baseHp: 165,
    },
  ],
};

// ── Battle sim result types ──────────────────────────────────────────
export type MilestoneReason =
  | "Type advantage"
  | "Type disadvantage"
  | "Move combo"
  | "Lucky speed"
  | "Tankiness"
  | "IV edge"
  | "Smart switch"
  | "Underdog"
  | "Critical save"
  | "STAB chain";

export interface Milestone {
  yourSpecies: string;
  yourDex: number;
  oppSpecies: string;
  oppDex: number;
  winner: "you" | "opp";
  headline: string;
  body: string;
  reasons: MilestoneReason[];
}

export interface BattleResult {
  yourTeamName: string;
  oppTeamName: string;
  yourScore: number;
  oppScore: number;
  outcome: "victory" | "defeat";
  milestones: Milestone[];
}

// Pre-scripted result for the demo. Deterministic. Prod build replaces
// with a zero-state placeholder that runSim never surfaces because the
// engine path is used instead in prod.
const EMPTY_RESULT: BattleResult = {
  yourTeamName: "", oppTeamName: "", yourScore: 0, oppScore: 0,
  outcome: "victory", milestones: [],
};
const DEMO_RESULT: BattleResult = import.meta.env.PROD ? EMPTY_RESULT : {
  yourTeamName: INFERNAL_TEAM.name,
  oppTeamName: MIA_TEAM.name,
  yourScore: 4,
  oppScore: 2,
  outcome: "victory",
  milestones: [
    {
      yourSpecies: "Annihilape", yourDex: 979,
      oppSpecies: "Garchomp", oppDex: 445,
      winner: "you",
      headline: "Annihilape outpaced Garchomp",
      body: "Your Annihilape's Ice Punch hit Garchomp's 4x Ice weakness for 196 damage, beating Earthquake to the KO. Lucky catch and 96% IV gave the speed edge.",
      reasons: ["Type advantage", "Lucky speed", "Move combo"],
    },
    {
      yourSpecies: "Zamazenta", yourDex: 889,
      oppSpecies: "Metagross", oppDex: 376,
      winner: "you",
      headline: "Zamazenta crushed Metagross with STAB Behemoth Bash",
      body: "Hundo Zamazenta with maxed Steel STAB chained Metal Claw into Behemoth Bash. The Steel-on-Steel chip wasn't pretty, but Behemoth Bash double-effective on Steel types finished it.",
      reasons: ["STAB chain", "IV edge", "Move combo"],
    },
    {
      yourSpecies: "Giratina", yourDex: 487,
      oppSpecies: "Togekiss", oppDex: 468,
      winner: "opp",
      headline: "Togekiss walled Giratina",
      body: "Charm chip damage outlasted Giratina's HP pool. Fairy resistance on Dragon Pulse meant your charge moves did half. A Steel slot here would have flipped it.",
      reasons: ["Type disadvantage"],
    },
    {
      yourSpecies: "Gholdengo", yourDex: 1000,
      oppSpecies: "Togekiss", oppDex: 468,
      winner: "you",
      headline: "Smart switch into Gholdengo",
      body: "The sim swapped to Gholdengo on the Charm read. Steel resists Fairy, and Make It Rain hit hard. Pivot strategy paid off, exactly what Gholdengo's slot is built for.",
      reasons: ["Smart switch", "Type advantage"],
    },
    {
      yourSpecies: "Kyogre", yourDex: 382,
      oppSpecies: "Tyranitar", oppDex: 248,
      winner: "opp",
      headline: "Tyranitar's Stone Edge ended Kyogre",
      body: "Shadow Kyogre at level 35 with 67% IV couldn't out-bulk Tyranitar's Stone Edge spam. A higher-IV Lucky Kyogre would have closed the gap. (See Coaching below.)",
      reasons: ["IV edge", "Type disadvantage"],
    },
    {
      yourSpecies: "Magnezone", yourDex: 462,
      oppSpecies: "Salamence", oppDex: 373,
      winner: "you",
      headline: "Magnezone punched up against Salamence",
      body: "Underdog moment. Wild Charge crit at 6% HP, surviving Salamence's Outrage by a sliver. Magnezone's Steel/Electric coverage made Salamence's Flying typing a liability.",
      reasons: ["Underdog", "Critical save", "Type advantage"],
    },
  ],
};

// ── Trade execution types ────────────────────────────────────────────
export type TradeStep = "confirm" | "both-confirm" | "upload" | "complete";

export interface PendingTrade {
  /** Pokémon you're giving. */
  yours: { species: string; dexNumber: number; cp: number; ivPercent: number };
  /** Pokémon you're receiving from friend. */
  theirs: { species: string; dexNumber: number; cp: number; ivPercent: number };
  friendName: string;
  step: TradeStep;
  miaConfirmed: boolean;
}

const STUB_TRADE: PendingTrade = import.meta.env.PROD ? {
  yours: { species: "", dexNumber: 0, cp: 0, ivPercent: 0 },
  theirs: { species: "", dexNumber: 0, cp: 0, ivPercent: 0 },
  friendName: "",
  step: "confirm",
  miaConfirmed: false,
} : {
  yours: { species: "Palkia", dexNumber: 484, cp: 2920, ivPercent: 89 },
  theirs: { species: "Kyogre", dexNumber: 382, cp: 3010, ivPercent: 96 },
  friendName: "Mia",
  step: "confirm",
  miaConfirmed: false,
};

interface BattleState {
  yourTeam: BattleTeam;
  oppTeam: BattleTeam;
  setYourTeam: (t: BattleTeam) => void;
  result: BattleResult | null;
  runSim: () => void;
  resetSim: () => void;

  // Trade execution
  pendingTrade: PendingTrade | null;
  startTrade: (t: Partial<PendingTrade>) => void;
  advanceTradeStep: (s: TradeStep) => void;
  setMiaConfirmed: (b: boolean) => void;
  closeTrade: () => void;
  tradesCompleted: number;
  completeTrade: () => void;
}

const BattleContext = createContext<BattleState | null>(null);

// In production mode, the user's team starts empty. They populate it from
// their Box Scan roster on the BattleTeam page. Audit R5: no hardcoded
// species names in marketing/seed copy for production builds.
const EMPTY_TEAM: BattleTeam = { name: "Your Team", trainer: "You", slots: [], rating: 0 };
const EMPTY_OPP_TEAM: BattleTeam = { name: "Opponent", trainer: "", slots: [], rating: 0 };

function maskTeam(team: BattleTeam): BattleTeam {
  if (getAppMode() !== "screenshot") return team;
  return {
    ...team,
    name: "Your Team",
    slots: team.slots.map((s) => ({ ...s, species: displayName(s.species, s.dexNumber) })),
  };
}

export function BattleProvider({ children }: { children: ReactNode }) {
  const startEmpty = inProductionMode();
  const [yourTeam, setYourTeam] = useState<BattleTeam>(
    startEmpty ? EMPTY_TEAM : maskTeam(INFERNAL_TEAM)
  );
  const [oppTeam] = useState<BattleTeam>(
    startEmpty ? EMPTY_OPP_TEAM : maskTeam(MIA_TEAM)
  );
  const [result, setResult] = useState<BattleResult | null>(null);
  const [pendingTrade, setPendingTrade] = useState<PendingTrade | null>(null);
  const [tradesCompleted, setTradesCompleted] = useState(0);

  const runSim = useCallback(() => {
    // For the prototype, we use the pre-scripted result (deterministic, narrative).
    // The "engine" lives below in computeBattle for completeness; we only call it
    // to avoid dead code warnings, but we surface DEMO_RESULT for stable copy.
    void computeBattle(yourTeam, oppTeam);
    setResult(DEMO_RESULT);
  }, [yourTeam, oppTeam]);

  const resetSim = () => setResult(null);

  const startTrade = (t: Partial<PendingTrade>) =>
    setPendingTrade({ ...STUB_TRADE, ...t, step: "confirm", miaConfirmed: false });
  const advanceTradeStep = (s: TradeStep) =>
    setPendingTrade((prev) => (prev ? { ...prev, step: s } : prev));
  const setMiaConfirmed = (b: boolean) =>
    setPendingTrade((prev) => (prev ? { ...prev, miaConfirmed: b } : prev));
  const closeTrade = () => setPendingTrade(null);
  const completeTrade = () => setTradesCompleted((n) => n + 1);

  return (
    <BattleContext.Provider
      value={{
        yourTeam, oppTeam, setYourTeam,
        result, runSim, resetSim,
        pendingTrade, startTrade, advanceTradeStep, setMiaConfirmed, closeTrade,
        tradesCompleted, completeTrade,
      }}
    >
      {children}
    </BattleContext.Provider>
  );
}

export function useBattle() {
  const ctx = useContext(BattleContext);
  if (!ctx) throw new Error("useBattle must be used inside BattleProvider");
  return ctx;
}

// ── Sim engine (deterministic, illustrative) ─────────────────────────
// Returns an internal score; we don't surface this directly because we want
// the narrative milestones to stay quotable. Kept here so the engine logic
// is real, even if we ship the pre-scripted demo result.
function typeMultiplier(atkType: string, defTypes: string[]): number {
  const chart: Record<string, { weak: string[]; resist: string[]; immune: string[] }> = {
    fire:     { weak: ["grass", "ice", "bug", "steel"], resist: ["fire", "water", "rock", "dragon"], immune: [] },
    water:    { weak: ["fire", "ground", "rock"], resist: ["water", "grass", "dragon"], immune: [] },
    grass:    { weak: ["water", "ground", "rock"], resist: ["fire", "grass", "poison", "flying", "bug", "dragon", "steel"], immune: [] },
    electric: { weak: ["water", "flying"], resist: ["electric", "grass", "dragon"], immune: ["ground"] },
    ice:      { weak: ["grass", "ground", "flying", "dragon"], resist: ["ice"], immune: [] },
    fighting: { weak: ["normal", "ice", "rock", "dark", "steel"], resist: ["psychic", "fairy", "flying"], immune: ["ghost"] },
    ground:   { weak: ["fire", "electric", "poison", "rock", "steel"], resist: ["grass", "bug"], immune: ["flying"] },
    flying:   { weak: ["grass", "fighting", "bug"], resist: ["electric", "rock", "steel"], immune: [] },
    psychic:  { weak: ["fighting", "poison"], resist: ["psychic", "steel"], immune: ["dark"] },
    bug:      { weak: ["grass", "psychic", "dark"], resist: ["fire", "fighting", "poison", "flying", "ghost", "steel", "fairy"], immune: [] },
    rock:     { weak: ["fire", "ice", "flying", "bug"], resist: ["fighting", "ground", "steel"], immune: [] },
    ghost:    { weak: ["psychic", "ghost"], resist: ["dark"], immune: ["normal", "fighting"] },
    dragon:   { weak: ["dragon"], resist: ["steel"], immune: ["fairy"] },
    dark:     { weak: ["psychic", "ghost"], resist: ["fighting", "dark", "fairy"], immune: [] },
    steel:    { weak: ["fire", "fighting", "ground"], resist: ["ice", "rock", "fairy"], immune: [] },
    fairy:    { weak: ["fighting", "dragon", "dark"], resist: ["fire", "poison", "steel"], immune: [] },
    poison:   { weak: ["grass", "fairy"], resist: ["poison", "ground", "rock", "ghost"], immune: ["steel"] },
    normal:   { weak: [], resist: [], immune: ["ghost", "rock", "steel"] },
  };
  let mult = 1;
  for (const dt of defTypes) {
    const c = chart[atkType];
    if (!c) continue;
    if (c.immune.includes(dt)) return 0;
    if (c.weak.includes(dt)) mult *= 2;
    if (c.resist.includes(dt)) mult *= 0.5;
  }
  return mult;
}

function computeBattle(your: BattleTeam, opp: BattleTeam): { yourScore: number; oppScore: number } {
  let y = 0, o = 0;
  const len = Math.min(your.slots.length, opp.slots.length);
  for (let i = 0; i < len; i++) {
    const a = your.slots[i], b = opp.slots[i];
    const ivA = 1 + a.ivPercent / 200;
    const ivB = 1 + b.ivPercent / 200;
    const atkA = a.baseAtk * ivA;
    const atkB = b.baseAtk * ivB;
    const hpA = a.baseHp * ivA;
    const hpB = b.baseHp * ivB;
    const tA = typeMultiplier(a.types[0] ?? "normal", b.types);
    const tB = typeMultiplier(b.types[0] ?? "normal", a.types);
    const dpsA = (atkA * tA) / Math.max(50, b.baseDef);
    const dpsB = (atkB * tB) / Math.max(50, a.baseDef);
    const ttkA = hpB / Math.max(0.1, dpsA);
    const ttkB = hpA / Math.max(0.1, dpsB);
    if (ttkA < ttkB) y++; else o++;
  }
  return { yourScore: y, oppScore: o };
}

// Exports for pages that need to render team selection candidates.
export const SAMPLE_INFERNAL_TEAM = INFERNAL_TEAM;
export const SAMPLE_MIA_TEAM = MIA_TEAM;
